import {
  FluidColor,
  FluidFlexAlignment,
  FluidFormElementTranslationOptions,
  FluidOption,
  FluidPopoverTranslationOptions,
  FluidRadioSelectorType,
} from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import { FormLabel } from '../../../common/fluid-common-form.components';
import { InfoPopover } from '../../../common/fluid-common-icon.components';
import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';
import { translateWithFallback } from '../../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../../utils/utils';

export type FluidRadioGroupTranslationOptions =
  FluidFormElementTranslationOptions;
export type TranslatableRadioOption = FluidOption & {
  fluidTranslate?: {
    label?: string;
    tooltip?: string;
    helpText?: FluidPopoverTranslationOptions;
  };
};
interface RadioGroupProps extends FluidFunctionalComponentProps {
  host: any;
  options: TranslatableRadioOption[];
  controlName: string;
  markTouchedAndValidate: Function;
  update: Function;
  selectorType: FluidRadioSelectorType;
  verticalLayout?: boolean;
  maxWidthPx?: number;
  maxWidthPercent?: number;
  questionWidth?: number;
  setRef: (el) => void;
  i18nStore?: FluidI18nStore;
}

// ================================================================ //
// -- Public Functional Components

export const InlineQuestionStyle: FunctionalComponent<RadioGroupProps> = (
  props: RadioGroupProps
) => {
  const { questionWidth = 6, host } = props;

  const verticalAlignment = Object.values(FluidFlexAlignment).includes(
    host.verticalAlignment
  )
    ? host.verticalAlignment
    : FluidFlexAlignment.CENTER;

  return (
    <fluid-grid remove-spacing={true}>
      <fluid-grid-item
        col={12}
        colMd={questionWidth}
        alignSelf={verticalAlignment}>
        <FormLabel
          host={host}
          resetPosition={true}
          inlineQuestion={true}
          class='inline-question-label'
        />
      </fluid-grid-item>
      <fluid-grid-item
        col={12}
        colMd={12 - questionWidth}
        class='inline-form-container'>
        <DefaultRadioGroup {...props} />
        <ButtonRadioGroup {...props} />
      </fluid-grid-item>
    </fluid-grid>
  );
};

/**
 * Renders default HTML radio buttons as the radio group
 * @param props
 * @constructor
 */
export const DefaultRadioGroup: FunctionalComponent<RadioGroupProps> = (
  props: RadioGroupProps
) => {
  const {
    options,
    controlName,
    markTouchedAndValidate,
    update,
    host,
    verticalLayout,
    selectorType,
    maxWidthPx,
    maxWidthPercent,
    setRef,
    i18nStore,
  } = props;
  if (selectorType === FluidRadioSelectorType.DEFAULT) {
    return options?.map((option: TranslatableRadioOption, idx: number) => {
      const optionId = `${controlName}_${idx}`;
      const optionStyle = {
        'max-width':
          (!!maxWidthPx && `${maxWidthPx}px`) ||
          (!!maxWidthPercent && `${maxWidthPercent}%`),
        display:
          maxWidthPx || maxWidthPercent || verticalLayout
            ? 'block'
            : 'inline-block',
      };

      let popoverRef: HTMLFluidPopoverElement;
      const handleFocusChange = (hasFocus: boolean) => {
        if (!popoverRef || (hasFocus && !hasFocusVisible())) return;
        popoverRef.open = hasFocus;
      };
      const isSelected = (option: FluidOption) =>
        `${option.value}` === `${host._elementState.value}`;

      return (
        <span
          style={optionStyle}
          class={{
            'fluid-radio-option': true,
            'vertical-layout': verticalLayout,
            disabled: host.disabled || host.readonly || option.disabled,
          }}>
          <input
            type='radio'
            class='radio-option-input'
            id={optionId}
            ref={(el) =>
              (isSelected(option) || !host._elementRef) && setRef(el)
            }
            name={controlName}
            value={option.value}
            disabled={host.disabled || host.readonly || option.disabled}
            checked={isSelected(option)}
            onChange={(event: InputEvent) => update(host, event)}
            onFocus={!!option.helpText && (() => handleFocusChange(true))}
            onBlur={() => {
              markTouchedAndValidate();
              handleFocusChange(false);
            }}
          />
          <label
            class='radio-option-label'
            htmlFor={optionId}
            title={translateWithFallback(
              i18nStore,
              option.fluidTranslate?.tooltip,
              option.tooltip
            )}>
            {translateWithFallback(
              i18nStore,
              option.fluidTranslate?.label,
              option.label
            )}
          </label>
          <InfoPopover
            tabIndex={-1}
            fluidTranslate={option.fluidTranslate?.helpText}
            ref={(el) => (popoverRef = el as HTMLFluidPopoverElement)}
            helpText={option.helpText}
            theme={host.theme}
            iconColor={
              !host.disabled && !host.readonly && !option.disabled
                ? FluidColor.BLUE
                : null // Inherits "disabled" text color when null
            }
          />
        </span>
      );
    });
  }
};

/**
 * ButtonRadioGroup - Renders a Button Group instead of default radio buttons
 */
export const ButtonRadioGroup: FunctionalComponent<RadioGroupProps> = (
  props: RadioGroupProps
) => {
  const {
    host,
    controlName,
    options,
    markTouchedAndValidate,
    update,
    selectorType,
    setRef,
  } = props;

  if (selectorType !== FluidRadioSelectorType.BUTTON) return;

  const onSelected = (value) => {
    markTouchedAndValidate();
    update(host, {
      target: {
        value: `${value ?? ''}`,
      },
    });
  };

  return (
    <fluid-button-group
      name={controlName}
      ref={(el) => setRef(el)}
      options={options}
      selectedValue={host._elementState.value}
      preventDefault={true}
      disabled={host.disabled || host.readonly}
      onSelected={(event) => onSelected(event.detail)}
      onFocusout={() => markTouchedAndValidate()}
      theme={host.theme}
      overrideGlobalTheme={true}
    />
  );
};

// ================================================================ //
// -- Functional Component Utilities (Private)

const hasFocusVisible = (): boolean =>
  Utils.getActiveElement().matches(':focus-visible');
